import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import BildquellenPage from "./pages/BildquellenPage";
import UeberUnsPage from "./pages/UeberUnsPage";
import LoesungenPage from "./pages/LoesungenPage";
import ErstberatungPage from "./pages/ErstberatungPage";
import SocialMediaShowcasePage from "./pages/SocialMediaShowcasePage";
import SmartcasesPage from "./pages/smartcases/SmartcasesPage";
import SmartcaseDetailPage from "./pages/smartcases/SmartcaseDetailPage";
import InsightsPage from "./pages/insights/InsightsPage";
import InsightsArticlePage from "./pages/insights/InsightsArticlePage";
import QRRedirectPage from "./pages/QRRedirectPage";
import { RouteSeo } from "./components/seo/RouteSeo";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import RegistrationPendingPage from "./pages/auth/RegistrationPendingPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import PendingApprovalPage from "./pages/auth/PendingApprovalPage";

// Portal (single page)
import PortalPage from "./pages/portal/PortalPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCustomerDetail from "./pages/admin/AdminCustomerDetail";
import AdminConsultations from "./pages/admin/AdminConsultations";
import AdminContactRequests from "./pages/admin/AdminContactRequests";
import AdminCSVImport from "./pages/admin/AdminCSVImport";
import AdminCategories from "./pages/admin/AdminCategories";

// Root route: intercepts QR redirects on the subdomain (?location=...&code=... or legacy ?gruppe=...)
const RootRoute = () => {
  const params = new URLSearchParams(window.location.search);
  if ((params.get('location') || params.get('gruppe')) && params.get('code')) {
    return <QRRedirectPage />;
  }
  return <PublicLayout><HomePage /></PublicLayout>;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-1">
      {children}
    </main>
    <Footer />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect pending_approval users to waiting page (except admins)
  if (user?.status === 'pending_approval' && user?.role !== 'admin') {
    return <Navigate to="/freischaltung-ausstehend" replace />;
  }
  
  if (adminOnly && !isAdmin) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
};

// Auth Route (redirect if already logged in)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect pending_approval users to waiting page
    if (user?.status === 'pending_approval' && user?.role !== 'admin') {
      return <Navigate to="/freischaltung-ausstehend" replace />;
    }
    return <Navigate to={isAdmin ? "/admin" : "/portal"} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
    <RouteSeo />
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/loesungen" element={<LoesungenPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:topicSlug" element={<InsightsArticlePage />} />
      <Route path="/insights/:topicSlug/:articleSlug" element={<InsightsArticlePage />} />
      <Route path="/ueber-uns" element={<UeberUnsPage />} />
      <Route path="/erstberatung" element={<ErstberatungPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/bildquellen" element={<BildquellenPage />} />
      
      {/* Hidden Pages (not in navigation) */}
      <Route path="/social-media-showcase" element={<SocialMediaShowcasePage />} />
      <Route path="/smartcases" element={<SmartcasesPage />} />
      <Route path="/smartcases/:slug" element={<SmartcaseDetailPage />} />
      
      {/* QR Code Redirect (public, no layout) - new query-param format + legacy path format */}
      <Route path="/qr" element={<QRRedirectPage />} />
      <Route path="/qr/:userId/:codeId" element={<QRRedirectPage />} />
      
      {/* Auth Pages */}
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/registrierung-geprueft" element={<RegistrationPendingPage />} />
      <Route path="/freischaltung-ausstehend" element={<PendingApprovalPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
      <Route path="/passwort-reset" element={<ResetPasswordPage />} />
      <Route path="/einladung" element={<AcceptInvitePage />} />
      
      {/* Portal (Single Page - Protected) */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <PortalPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy route redirect */}
      <Route path="/dashboard/*" element={<Navigate to="/portal" replace />} />
      
      {/* Admin (Protected + Admin Only) */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminUsers />} />
        <Route path="customers/:userId" element={<AdminCustomerDetail />} />
        <Route path="consultations" element={<AdminConsultations />} />
        <Route path="contacts" element={<AdminContactRequests />} />
        <Route path="import" element={<AdminCSVImport />} />
        <Route path="categories" element={<AdminCategories />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
